import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import {
  debounceTime,
  delay,
  filter,
  first,
  take,
  takeUntil,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ApiErrorListModalComponent } from '@components/modals/api-error-list-modal/api-error-list-modal.component';
import {
  FlowBaseUrlEnum,
  DEFAULT_COOKIE_MODAL_TIMEOUT_MILLIS,
  FARE_CLASS_NOT_AVAILABLE_ERROR_CODE,
  MODAL_DIALOG_CENTER_LG,
  MODAL_DIALOG_CENTER,
  MODAL_THREE_TAILS_IMG,
  TOKEN_TIMEOUT_ERROR_CODE,
  CULTURE_ROUTE_PARAM,
  CultureKeyType,
  AGENT_AUTHENTICATION_ERROR_CODE,
  PAYMENT_GREATER_THAN_DUE_ERROR_CODE,
  PROMO_CODE_INVALID,
  SCROLL_CONTAINER_ID,
  SECONDS_PER_DAY,
  CULTURE_US,
  CULTURE_FRENCH,
  BOOKING_NOT_FOUND_ERROR_CODE,
} from '@constants/app.constants';
import { CmsSubKeys } from '@constants/cms.constants';

import {
  selectActiveDisruptionBanners,
  selectApiErrors,
  selectDoShowLoader,
  selectShouldUseMobileStyling,
  selectShowModifySearchWidget,
} from '@selectors/app.selector';
import { CookieConsentService } from '@services/cookie-consent/cookie-consent.service';
import { environment } from '@environments/environment';
import { ModalService } from '@services/modal/modal.service';

import {
  appApiClearErrors,
  appHandleGtmScript,
  appSetAutoSearch,
  appSetShowModifySearchWidget,
  appShowCookieModal,
  appStart,
  appStoreWindowSize, loadDynamicPagesConfig
} from '@actions/app.action';
import { sendPageViewEvent } from '@actions/gtm.action';
import {
  selectIsBookConfirmationPath,
  selectIsBookPath,
  selectIsFlightsPage,
  selectIsFlowOrTripsPage,
  selectIsFlowPage,
  selectIsHomePage,
  selectIsUpliftPage,
  selectQueryParam,
  selectRouteParam,
} from '@selectors/router.selector';
import { combineLatest, Subject } from 'rxjs';
import { BottomImageModalComponent } from '@components/modals/bottom-image-modal/bottom-image-modal.component';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { concatLatestFrom } from '@ngrx/operators';
import { selectAppCmsCopy, selectHomeCopy } from '@selectors/cms.selector';
import { ResizeObserverService } from '@services/resize-observer/resize-observer.service';
import { CommonModule, DOCUMENT } from '@angular/common';
import { WindowService } from '@services/window/window.service';
import { AppCopy } from '@models/cms/app.model';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { HttpClientModule, HttpClientJsonpModule } from '@angular/common/http';
import { FooterComponent } from '@components/footer/footer.component';
import { HeaderComponent } from '@components/header/header.component';
import { LoaderComponent } from '@components/loader/loader.component';
import { DisruptionBannerComponent } from '@components/disruption-banner/disruption-banner.component';
import { LetDirective } from '@directives/app-let.directive';
import {
  CultureCodeUpdateType,
  updateCultureCode,
} from '@actions/culture.action';
import { selectCultures } from '@selectors/resource.selector';
import { SearchWidgetComponent } from '@components/search-widget/search-widget.component';
import { Link } from '@models/cms/link.model';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { FeatureFlagsEnum } from '@models/environment.type';
import { ApiError } from '@models/app.models';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { UtilitiesService } from '@services/utilities/utilities.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { SvgDefinitionsComponent } from '@components/svg-icon/svg-definitions/svg-definitions.component';
import { VerifiedByNorseComponent } from './features/home/components/verified-by-norse/verified-by-norse.component';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { MoneyBackGuaranteePopupComponent } from '@components/money-back-guarantee-popup/money-back-guarantee-popup.component';
import { selectAvailabilitySelectedJourneysState } from '@selectors/availability.selector';
import { HotjarService } from '@services/hotjar/hotjar.service';
import { SearchComponent } from './features/search/search.component';
import { availabilityClearPromoCode } from '@actions/availability.action';
import { selectBooking } from '@selectors/booking.selector';
import { GeocodeService } from '@services/geocode/geocode.service';
import { FlightsLoaderComponent } from '@components/flights-loader/flights-loader.component';
import { ScrollService } from '@services/scroll/scroll.service';
import { PlatformService } from '@services/platform/platform.service';
import { MultiAppService } from '@services/multiapp/multiapp.service';
import { TooltipComponent } from '@components/tooltip/tooltip.component';
import { TooltipService } from '@components/tooltip/tooltip.service';
import { cmsDisruptionBannersLoad } from '@actions/cms.action';
import { TooltipContentModel } from '@models/tooltip-content-model';
import { CloudflareService } from '@services/cloudflare/cloudflare.service';
import { CookieStorageService } from '@services/cookie-storage/cookie-storage.service';
import { getLocaleFromUrl } from '@utils/get-locale-from-url.utils';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    // bootstrap
    TypeaheadModule,
    ModalModule,
    ButtonsModule,
    AccordionModule,
    AlertModule,
    CollapseModule,
    BsDatepickerModule,
    TooltipModule,
    //
    HeaderComponent,
    FooterComponent,
    DisruptionBannerComponent,
    HttpClientModule,
    HttpClientJsonpModule,
    LoaderComponent,
    LetDirective,
    RouterModule,
    SearchWidgetComponent,
    SvgDefinitionsComponent,
    VerifiedByNorseComponent,
    MoneyBackGuaranteePopupComponent,
    SearchComponent,
    FlightsLoaderComponent,
    TooltipComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('appHeader') appHeader: ElementRef<HTMLElement> | undefined;
  @ViewChild('disruptionBanners') disruptionBanners: ElementRef<HTMLElement> | undefined;

  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  onDocumentClick(event: MouseEvent | TouchEvent): void {
    let target: HTMLElement | null = null;

    if (event instanceof MouseEvent) {
      target = event.target as HTMLElement;
    } else if (event.touches && event.touches.length > 0) {
      target = (event.touches[0] as Touch).target as HTMLElement;
    }

    if (target) {
      this.utilitiesService.documentClickedTarget.next(target);
    }
  }

  doShowLoader$ = this.store.select(selectDoShowLoader);
  appCopy$ = this.store.select(selectAppCmsCopy);
  homeCopy$ = this.store.select(selectHomeCopy);
  appCopy: AppCopy | undefined;
  apiErrors$ = this.store.select(selectApiErrors);
  mobileStyle$ = this.store.select(selectShouldUseMobileStyling);
  cookieQueryParam$ = this.store.select(selectQueryParam('cm'));
  readonly SHOW = 'show';
  readonly CmsSubKeys = CmsSubKeys;
  readonly FeatureFlagsEnum = FeatureFlagsEnum;
  readonly EnvironmentService = EnvironmentService;
  readonly GrowthBookFeatureKeys = GrowthBookFeatureKeys;
  readonly BOOKING_KEY = 'n0_booking_key';
  onDestroyed$ = new Subject<void>();
  headerHeightOffset = 0;
  disruptionBannersHeightOffset = 0;
  footerHeightOffset = 0;
  appHeight: number;
  contentHeight: number;
  mainMinHeight: number;
  headerElement: HTMLElement | null;
  footerElement: HTMLElement | null;
  displayTooltip: boolean | undefined;
  setHeightAdjustments = (): void => {
    this.headerHeightOffset = this.appHeader?.nativeElement.offsetHeight ?? 0;
    this.disruptionBannersHeightOffset =
      this.disruptionBanners?.nativeElement.offsetHeight ?? 0;
    this.footerHeightOffset = this.footerElement?.offsetHeight ?? 0;
    this.appHeight = this.platformService.height();
    this.contentHeight =
      this.appHeight -
      this.headerHeightOffset -
      this.disruptionBannersHeightOffset;
    this.mainMinHeight = this.contentHeight - this.footerHeightOffset - 4;
    this.cd.detectChanges();
  };
  /* istanbul ignore next */
  resizeObs = this.resizeObserverService.getObserver(this.setHeightAdjustments);
  currentCulture: string | undefined;
  culture$ = this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM));
  isFlowPage$ = this.store.select(selectIsFlowPage);
  isFlowOrTripsPage$ = this.store.select(selectIsFlowOrTripsPage);
  isUpliftPage$ = this.store.select(selectIsUpliftPage);
  getShowModifySearchWidget$ = this.store.select(selectShowModifySearchWidget);
  isHomePage$ = this.store.select(selectIsHomePage);
  isFlightsPage$ = this.store.select(selectIsFlightsPage);
  selectedJourneys$ = this.store.select(
    selectAvailabilitySelectedJourneysState,
  );
  isBookPath$ = this.store.select(selectIsBookPath);
  isBookConfirmationPage$ = this.store.select(selectIsBookConfirmationPath);
  booking$ = this.store.select(selectBooking);
  disruptionBanners$ = this.store.select(selectActiveDisruptionBanners);
  storageEvent$ = WindowService.storageEvent;
  flightSearchInProgress$ = this.utilitiesService.onFlightsSearchInProgress$();
  isMobileDevice = false;
  hideChatbot = true;
  addFixedClass = false;
  hideTooltipOnScroll: boolean = true;
  readonly scrollContainerId = SCROLL_CONTAINER_ID;

  get is1433TopMenuReorderingEnabled(): boolean {
    return this.gbs.isVariantOfExperimentEnabled(
      'feat-001433-top-menu-reordering',
      'b',
    );
  }

  get active1416USVariant(): string {
    return this.gbs.growthBook.getFeatureValue(
      'feat-001416-change-search-area-to-media-content-US',
      '',
    );
  }

  get active1416ROWVariant(): string {
    return this.gbs.growthBook.getFeatureValue(
      'feat-001416-change-search-area-to-media-content-ROW',
      '',
    );
  }

  get background() {
    if (this.active1416USVariant === 'b') {
      return 'SearchBackgroundBUS';
    } else if (this.active1416USVariant === 'c') {
      return 'SearchBackgroundCUS';
    } else if (this.active1416USVariant === 'd') {
      return 'SearchBackgroundDUS';
    } else if (this.active1416ROWVariant === 'b') {
      return 'SearchBackgroundBROW';
    } else if (this.active1416ROWVariant === 'c') {
      return 'SearchBackgroundCROW';
    } else if (this.active1416ROWVariant === 'd') {
      return 'SearchBackgroundDROW';
    }
  }

  get isVideoBackground(): boolean {
    return (
      this.active1416USVariant === 'c' || this.active1416ROWVariant === 'c'
    );
  }

  constructor(
    private store: Store,
    public modalService: ModalService,
    private cookieConsentService: CookieConsentService,
    public router: Router,
    @Inject(DOCUMENT) private doc: Document,
    private resizeObserverService: ResizeObserverService,
    private cd: ChangeDetectorRef,
    public featureFlagService: FeatureFlagService,
    private utilitiesService: UtilitiesService,
    public gbs: GrowthBookService,
    public mas: MultiAppService,
    private hotjarService: HotjarService,
    private location: Location,
    private geocodeService: GeocodeService,
    private scrollService: ScrollService,
    private platformService: PlatformService,
    private tooltipService: TooltipService,
    private cloudflareService: CloudflareService,
    private cookieStorageService: CookieStorageService,
  ) {
    this.store.dispatch(
      appStart({
        platforms: platformService.platforms(),
        width: this.platformService.width(),
        height: this.platformService.height(),
        isMobileFromTablet: this.is1433TopMenuReorderingEnabled,
      }),
    );
  }

  ngOnInit(): void {
    this.mas.initMobileApp();

    const feat1522disableGoogleGeoLookupValue =
      this.gbs.growthBook.getFeatureValue(
        'feat-002341-cloudflare-geolocation',
        '',
      );
    if (feat1522disableGoogleGeoLookupValue === 'b') {
      this.cloudflareService.updateCloudflareMeta(this.culture$);
    }

    if (
      !this.gbs.isVariantOfExperimentEnabled(
        'feat-002389-turning-off-cultures',
        'b',
      )
    ) {
      //this.geocodeService.navigateToGeocodedCulture(); TODO: 2341 has rewritten this geocode-naviation part. ADO #2545
    } else {
      const locale = getLocaleFromUrl(this.location.path());
      let newLocale: string = locale || '';
      if (locale !== CULTURE_FRENCH && locale !== CULTURE_US) {
        newLocale = CULTURE_US;
      }
      this.cookieStorageService.setItem(
        CookieStorageService.cookieCulture,
        newLocale,
        180 * SECONDS_PER_DAY,
      );
      this.router.navigateByUrl(
        this.location.path().replace(`/${locale}`, `/${newLocale}`),
      );
    }

    this.appCopy$.pipe(first((c) => !!c)).subscribe(() => {
      if (this.cookieConsentService.shouldShowModal()) {
        const timeout = Number(environment.cookieTimeoutMillis);
        setTimeout(() => {
          this.store.dispatch(appShowCookieModal());
        }, timeout ?? DEFAULT_COOKIE_MODAL_TIMEOUT_MILLIS);
      } else {
        this.store.dispatch(appHandleGtmScript());
      }
    });
    this.appCopy$
      .pipe(
        filter((c) => !!c),
        takeUntil(this.onDestroyed$),
      )
      .subscribe((c: AppCopy) => (this.appCopy = c));

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = (<NavigationEnd>event).urlAfterRedirects;
        this.store.dispatch(sendPageViewEvent({ url }));
        this.setHeightAdjustments();
      });

    combineLatest([this.culture$, this.store.select(selectCultures)])
      .pipe(
        filter(
          ([cultureCode, cultures]) =>
            !!cultures?.some((c) => c.cultureCode === cultureCode),
        ),
        takeUntil(this.onDestroyed$),
      )
      .subscribe(([cultureCode]) => {
        if (this.currentCulture !== cultureCode) {
          this.currentCulture = cultureCode;
          this.store.dispatch(
            updateCultureCode({
              from: CultureCodeUpdateType.Url,
              code: cultureCode as CultureKeyType,
            }),
          );
        }
      });

    let apiErrorModalRef: BsModalRef | undefined;
    this.apiErrors$
      .pipe(
        concatLatestFrom(() => [this.appCopy$, this.culture$]),
        filter(([e, copy, _culture]) => !!copy && e && e.length > 0),
      )
      .subscribe(([e, appCopy, culture]) => {
        if (e.find((e) => e.code === PROMO_CODE_INVALID)) {
          this.goBack();
          this.store.dispatch(availabilityClearPromoCode());
        }
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        // we know copy is non-null because of the filter above
        if (e.find((e) => e.code === FARE_CLASS_NOT_AVAILABLE_ERROR_CODE)) {
          this.modalService
            .show(BottomImageModalComponent, {
              class: MODAL_DIALOG_CENTER_LG,
              initialState: {
                btnText: appCopy!.reselect,
                text: appCopy!.fareClassNotAvailableError,
                img: MODAL_THREE_TAILS_IMG,
                altText: appCopy!.reselect,
              },
            })
            .onHide?.pipe(take(1))
            .subscribe(() => {
              this.router.navigate([
                `/${culture}/${FlowBaseUrlEnum.BOOK}/${FlowPathEnum.PAYMENT}`,
              ]); //TODO: Check modify flow rules and if needed dynamically change the URL here.
              this.store.dispatch(
                appSetAutoSearch({ payload: { search: true } }),
              );
            });
        } else if (
          e.length > 0 &&
          e.some(
            (e) =>
              e.code !== TOKEN_TIMEOUT_ERROR_CODE &&
              e.code !== AGENT_AUTHENTICATION_ERROR_CODE &&
              e.code !== PAYMENT_GREATER_THAN_DUE_ERROR_CODE,
          )
        ) {

          // don't show modal when only token timeout errors ^
          if (!apiErrorModalRef) {
            const isBookingNotFound = this.checkBookingNotFoundError(e);
            if (isBookingNotFound) {
              return;
            }

            apiErrorModalRef = this.modalService.show(
              ApiErrorListModalComponent,
              {
                class: MODAL_DIALOG_CENTER,
                initialState: {
                  errors: e,
                  cmsApiErrorCopy: appCopy![CmsSubKeys.appApiErrors],
                },
              },
            );

            apiErrorModalRef.onHidden?.pipe(take(1)).subscribe(() => {
              apiErrorModalRef = undefined;
              this.store.dispatch(appApiClearErrors());
            });
          } else {
            const modalContent = apiErrorModalRef.content as {
              errors: ApiError[];
            };
            if (modalContent) {
              modalContent.errors = e;
            }
          }
        }
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
      });

    /* istanbul ignore next */
    this.cookieQueryParam$
      .pipe(
        filter((p) => p === this.SHOW),
        takeUntil(this.onDestroyed$),
      )
      .subscribe(() => {
        this.store.dispatch(appShowCookieModal());
      });

    this.platformService
      .resize()
      .pipe(debounceTime(100), takeUntil(this.onDestroyed$))
      .subscribe(() => {
        this.setHeightAdjustments();
        this.store.dispatch(
          appStoreWindowSize({
            height: this.platformService.height(),
            width: this.platformService.width(),
            isMobileFromTablet: this.is1433TopMenuReorderingEnabled,
          }),
        );
      });
    this.sendHotjarFeedbackEvent();

    this.gbs.onChange$.pipe(takeUntil(this.onDestroyed$)).subscribe(() => {
      this.cd.detectChanges();
    });

    this.initTooltip();
    this.updateHeightAdjustmentsOnNotificationsChanges();
    this.fetchDistruptionBanners();
  }

  ngAfterViewChecked(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.scrollService.scrollToTop();
      });
    if (!this.headerElement || !this.footerElement) {
      this.headerElement = this.doc.querySelector('app-header');
      this.footerElement = this.doc.querySelector('footer');
      if (this.headerElement && this.footerElement) {
        this.resizeObs.observe(this.headerElement);
        this.resizeObs.observe(this.footerElement);
        this.setHeightAdjustments();
        this.initScrollObserver();
      }
    }
  }

  checkBookingNotFoundError(e: ApiError[]): boolean {
    const isBookingNotFound = e.find((e) => e.code === BOOKING_NOT_FOUND_ERROR_CODE);
    const feature3240Key = 'feat-003240-your-trips-page-improvement';
    const isFeat3240Enabled = this.gbs.growthBook.isOn(feature3240Key);
    const feat3240Value = this.gbs.growthBook.getFeatureValue(feature3240Key, '');
    const feat3240IsAllowedVariant = feat3240Value === 'b' || feat3240Value === 'c';
    return !!isBookingNotFound && isFeat3240Enabled && feat3240IsAllowedVariant;
  }

  onBookClick(): void {
    this.store.dispatch(appSetShowModifySearchWidget({ show: true }));
  }

  onBottomMenuItemClick(link: Link): void {
    !!link.link && this.router.navigateByUrl(link.link);
  }

  ngOnDestroy(): void {
    this.headerElement && this.resizeObs.unobserve(this.headerElement);
    this.footerElement && this.resizeObs.unobserve(this.footerElement);
    this.onDestroyed$.next();
  }

  private sendHotjarFeedbackEvent(): void {
    combineLatest([
      this.culture$,
      this.isHomePage$,
      this.isBookPath$,
      this.isBookConfirmationPage$,
      this.gbs.onChange$,
    ])
      .pipe(
        debounceTime(2000),
        filter(
          ([culture, isHomePage, isBookPath, isBookConfirmationPage]) =>
            !isBookConfirmationPage && (isHomePage || isBookPath) && !!culture,
        ),
        takeUntil(this.onDestroyed$),
      )
      .subscribe(([culture]) => {
        if (
          this.hotjarService.isEnabled() &&
          this.gbs.growthBook.isOn(
            this.GrowthBookFeatureKeys.FEAT_US183_HOTJAR_FEEDBACK_BOOKINGFLOW,
          ) &&
          this.gbs.growthBook.getFeatureValue(
            this.GrowthBookFeatureKeys.FEAT_US183_HOTJAR_FEEDBACK_BOOKINGFLOW,
            '',
          ) === 'b'
        ) {
          switch (culture) {
            case 'en-US':
            case 'en-GB':
              this.hotjarService.event('enable_feedback_en');
              break;
            case 'nb-NO':
              this.hotjarService.event('enable_feedback_nb');
              break;
            case 'fr-FR':
              this.hotjarService.event('enable_feedback_fr');
              break;
            case 'de-DE':
              this.hotjarService.event('enable_feedback_de');
              break;
            case 'it-IT':
              this.hotjarService.event('enable_feedback_it');
              break;
            default:
              this.hotjarService.event('enable_feedback_en');
              break;
          }
        }
      });
  }

  onScrollTop(): void {
    this.scrollService.scrollToTop();
  }

  initTooltip(): void {
    this.tooltipService.tooltipContent
      .pipe(takeUntil(this.onDestroyed$))
      .subscribe((content: TooltipContentModel) => {
        this.hideTooltipOnScroll = !content.showOnClick;
      });
  }

  shouldShowBanner(banner: { websitePath?: string }): boolean {
    if (!banner.websitePath) {
      return true;
    }
    return this.router.url.split('/').some(
      (path) =>
        banner.websitePath
          ?.split(',')
          .map((p) => p.trim())
          .includes(path) ?? false,
    );
  }

  private initScrollObserver() {
    this.scrollService
      .onScrollStartObservable()
      .pipe(takeUntil(this.onDestroyed$))
      .subscribe(() => {
        if (this.hideTooltipOnScroll) {
          this.tooltipService.hideTooltip();
        }
      });
  }

  private goBack(): void {
    const canGoBack: boolean =
      !!this.router.getCurrentNavigation()?.previousNavigation;
    if (canGoBack) {
      this.location.back();
    } else {
      this.router.navigate(['/']);
    }
  }

  private fetchDistruptionBanners() {
    this.culture$
      .pipe(takeUntil(this.onDestroyed$))
      .subscribe((cultureCode) => {
        if (cultureCode) {
          this.store.dispatch(cmsDisruptionBannersLoad({ cultureCode }));
        }
      });
  }
  private updateHeightAdjustmentsOnNotificationsChanges(): void {
    this.disruptionBanners$.pipe(delay(10)).subscribe(() => {
      this.setHeightAdjustments();
    });
  }
}
